import {CommonModule, Location} from '@angular/common';
import { Component, OnInit, ViewChild} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DropDownComponent } from '../../components/drop-down/drop-down.component';
import { PacService } from '../../shared/services/pac.service';
import { DepositTypeService } from '../../shared/services/deposit-type.service';
import { DepositCategoryService } from '../../shared/services/deposit-category.service';
import { DepositType } from '../../models/deposit-type.model';
import { PaymentCategory } from '../../models/additional-pac-data.model';
import {catchError, EMPTY, switchMap, tap} from 'rxjs';
import { finalize } from 'rxjs/operators';
import { VirtualKeyboardComponent } from '../../components/virtual-keyboard/virtual-keyboard.component';
import { ConfirmationModalWithDynamicComponent } from '../../components/confirmation-modal-with-dynamic-component/confirmation-modal-with-dynamic-component.component';
import { CustomModalComponent } from '../../components/custom-modal/custom-modal.component';
import { ConfirmDepositDetailsComponent } from '../confirm-deposit-details/confirm-deposit-details.component';
import { DeriveCourtService } from '../../shared/services/derive-court.service';
import { ErrorBannerComponent} from '../../components/error-banner/error-banner.component';
import {AppConstants} from '../../shared/app-constants';
import { validateUEN } from '../../shared/uen-validator.util';
import {restrictToTwoDecimals} from '../../shared/utils/default-amount-utils';

@Component({
  selector: 'app-court-deposit',
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    DropDownComponent,
    VirtualKeyboardComponent,
    ConfirmationModalWithDynamicComponent,
    CustomModalComponent,
    ErrorBannerComponent
  ],
  templateUrl: './court-deposit.component.html',
  styleUrl: './court-deposit.component.scss',
  standalone: true
})
export class CourtDepositComponent implements OnInit {
  depositCategoryDropdownValues: { name: string; value: string }[] = [];
  depositTypeDropdownValues: { name: string; value: string }[] = [];
  pacNumber: string = '';
  caseNumber: string = '';
  depositCategory: string = '';
  depositCategoryDisplayValue: string = '';
  depositType: string = '';
  depositTypeDisplayValue: string = '';
  subCaseNumber: string = '';
  allDepositTypes!: DepositType[];
  payerId: string = '';
  contactNumber: string = '';
  emailId: string = '';
  payerName: string = '';
  amount: number | null = null;
  remarks: string = '';
  depositTypeSelected: any = null; // or however it's stored
  depositCategoryTouched: boolean = false;
  depositTypeTouched: boolean = false;
  isCaseNumberMandatory: boolean = true;
  isSubCaseNumberMandatory: boolean = true;
  courtName: string = '';
  depositCategorySelected: { name: string; value: string } | null = null;
  errorMessage: string | null = null;
  courtDropdownValues = [
    { name: 'State Courts', value: 'SC' },
    { name: 'Supreme Court', value: 'SupCt' },
    { name: 'Family Justice Courts', value: 'FJC' }
  ];

  payerIdInvalid: boolean = false;
  selectedCourt: string | null = null;
  courtTouched = false;
  amountErrorMessage: string = '';
  amountZero: boolean = false;
  isAmountPresentValid: boolean = false;
  isCourtApplicable: boolean = false;
  isEmailMandatory: boolean = false;
  isPayerIdMandatory: boolean = false;

  @ViewChild('abortModalRef') abortModalRef!: CustomModalComponent;
  @ViewChild('depositRecordModalRef') depositRecordModalRef!: ConfirmationModalWithDynamicComponent;
  isDepositCategoryHidden: boolean = false;

  constructor(private readonly router: Router,
              private readonly pacService: PacService,
              private readonly depositTypeService: DepositTypeService,
              private readonly depositCategoryTypeService: DepositCategoryService,
              private readonly deriveCourtService: DeriveCourtService,
              private readonly location: Location) {
  }

  onCourtSelected(selected: any) {
    this.selectedCourt = selected?.value || null;
  }


  fetchDepositTypes(): void {
    this.depositTypeService.fetchDepositTypes().pipe(
      tap(depositTypes => {
        this.allDepositTypes = depositTypes;
      }),
      catchError(err => {
        // Optionally show user-friendly message here
        return EMPTY; // Prevents error from propagating further
      }),
      finalize(() => {
      })
    ).subscribe();
  }

  onDepositTypeSelected(selectedDepositType: { name: string; value: string }) {
    this.depositTypeTouched = true;
    this.depositType = selectedDepositType.value;
    this.depositTypeDisplayValue = selectedDepositType.name;
    this.setCaseOrSubCaseMandatory();
  }

  onDepositCategorySelected(selectedCategory: { name: string; value: string }) {
    this.depositCategoryTouched = true;
    this.depositTypeDropdownValues = [];
    this.depositType = '';
    this.depositTypeDisplayValue = '';

    this.depositCategory = selectedCategory.value;
    this.depositCategoryDisplayValue = selectedCategory.name;
    this.depositCategorySelected = selectedCategory;

    this.depositTypeDropdownValues = (this.allDepositTypes || [])
      .filter(item => item.depositCategory === selectedCategory.value)
      .map(item => ({
        name: item.displayValueInPaymentPortal,
        value: item.shortCode
      }));
    this.setDepositFieldsMandatory(selectedCategory);
  }

  prepareDepositDetails() {
    const terminalId = localStorage.getItem('kioskCode');
    return  {
      paymentType: this.depositCategory,
      payerId: this.payerId.toLocaleUpperCase(),
      payerName: this.payerName,
      contactNumber: this.contactNumber,
      emailId: this.emailId,
      totalAmountPayable: this.amount || 0,
      amountPaid: 0,
      balancePayable: this.amount || 0,
      paymentCategory: PaymentCategory.COURT_DEPOSIT,
      court: this.courtName,
      terminalId: terminalId || '',
      depositDetailsInfo: {
        depositCategory: this.depositCategory,
        depositCategoryDisplayValue: this.depositCategoryDisplayValue,
        depositType: this.depositType,
        depositTypeDisplayName: this.depositTypeDisplayValue,
        caseNumber: this.caseNumber.toLocaleUpperCase(),
        subCaseNumber: this.subCaseNumber.toLocaleUpperCase(),
        remarks: this.remarks
      }
    };
  }

  processSubmittedDepositDetails() {
    const depositData = this.prepareDepositDetails();
    this.pacService.saveAdditionalInfo(depositData).pipe(
      tap(response => {
      }),
      catchError(err => {
        return EMPTY;
      }),
      finalize(() => {
      })
    ).subscribe(depositInfo => {
      this.router.navigate(['court-deposit-details'], {
        state: {depositInfo}
      }).catch(navErr => {
      });
    });
  }

  fetchCourtNameFromDepositDetails() {
    const depositData = this.prepareDepositDetails();
    if (depositData.paymentType === AppConstants.OTHER_DEPOSIT && this.selectedCourt != null) {
      this.courtName = this.selectedCourt;
      this.showDepositConfirmationPopup()
    } else {
      this.deriveCourtService.fetchCourtName(depositData).pipe(
        tap(response => {
          if (response) {
            this.errorMessage = null;
            this.courtName = response.courtName;
            this.showDepositConfirmationPopup();
          } else {
            this.errorMessage = AppConstants.CASE_NUMBER_INVALID;
          }
        }),
        catchError(err => {
          this.errorMessage = AppConstants.CASE_NUMBER_INVALID;
          return EMPTY;
        })
      ).subscribe();
    }
  }

  get isFormValid(): boolean {
    const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.emailId || '');
    const isContactValid = /^[689]\d{7}$/.test(this.contactNumber || '');
    const isCaseNumberValid = !this.isCaseNumberMandatory || !!this.caseNumber?.trim();
    const isSubCaseNumberValid = !this.isSubCaseNumberMandatory || !!this.subCaseNumber?.trim();
    const isPayerNameValid = !!this.payerName?.trim();
    let isPayerIdMandatory = this.getPayerIdMandatory(this.depositCategory);
    const isPayerIdValid = isPayerIdMandatory ? !!this.payerId?.trim() : true;

    let isContactInfoValid = false;
    if (this.depositCategory === AppConstants.SHERIFF_BAILIFF_DEPOSIT) {
      isContactInfoValid = isEmailValid;
    } else {
      isContactInfoValid = isEmailValid || isContactValid;
    }
    return (
      !!this.depositCategory &&
      !!this.depositType &&
      isCaseNumberValid &&
      isSubCaseNumberValid &&
      isPayerIdValid &&
      isPayerNameValid &&
      isContactInfoValid &&
      this.isAmountPresentValid &&
      !this.payerIdInvalid
    );
  }

  getPayerIdMandatory(depositCategory: string) {
    const notSICCorSIC = depositCategory !== AppConstants.SID && depositCategory !== AppConstants.SICC;
    return notSICCorSIC;
  }

  onClickHomeButton() {
    this.abortModalRef.showModal();
  }

  handleAbortModalClose(confirmed: boolean) {
    this.abortModalRef.hideModal();
    if (confirmed) {
      this.router.navigate([''])
    }
  }



  submitDepositDetails() {
    const hasEmail = this.emailId && this.emailId.trim() !== '';
    const hasContact = this.contactNumber && this.contactNumber.trim() !== '';

    if (!hasEmail && !hasContact) {
      this.errorMessage = 'Either an Email ID or Contact is required.';
      return;
    }

    this.fetchCourtNameFromDepositDetails();
  }

  showDepositConfirmationPopup() {
    const depositData = this.prepareDepositDetails();
    this.depositRecordModalRef.loadComponent(ConfirmDepositDetailsComponent, {"pacDetails": depositData});
  }

  handleConfirmationModalClose(confirmed: boolean) {
    if (confirmed) {
      // perform action
      this.processSubmittedDepositDetails();
    }
  }

  allowAlphaNumericOnly(event: KeyboardEvent) {
      const regex = /^[a-zA-Z0-9]*$/;
      const inputChar = event.key;
      if (!regex.test(inputChar)) {
        event.preventDefault();
      }
  }

  validateUENField() {
    this.payerIdInvalid = this.payerId.toLocaleUpperCase().trim() !== '' && !validateUEN(this.payerId.toLocaleUpperCase().trim());
  }

  setCaseOrSubCaseMandatory() {
    const matchedDepositTypeFound = this.allDepositTypes.find(
      item =>
        item.depositCategory === this.depositCategory &&
        item.displayValueInPaymentPortal === this.depositTypeDisplayValue
    );

    this.isCaseNumberMandatory = matchedDepositTypeFound?.isCaseNumberMandatory === true;
    this.isSubCaseNumberMandatory = matchedDepositTypeFound?.isSubCaseNumberMandatory === true;
  }

  ngOnInit(): void {
    const state = history.state;
    const selectedCategoryCode: string | null = state.depositCategorySelected;

    this.depositTypeService.fetchDepositTypes().pipe(
      tap(depositTypes => {
        this.allDepositTypes = depositTypes;
      }),
      switchMap(() => this.depositCategoryTypeService.fetchDepositCategory()),
      tap(depositCategory => {
        this.depositCategoryDropdownValues = [
          ...new Map(
            depositCategory.map(item => [item.dataValue, {
              name: item.dataValue,
              value: item.dataCode
            }])
          ).values()
        ] as { name: string; value: string }[];

        if (selectedCategoryCode) {
          const selectedCategory = this.depositCategoryDropdownValues.find(
            c => c.value === selectedCategoryCode
          );
          if (selectedCategory) {
            this.depositCategorySelected = selectedCategory;
            this.depositCategoryTouched = true;
            this.isDepositCategoryHidden = true;
            this.onDepositCategorySelected(selectedCategory);
          }
        }
      }),
      catchError(err => {
        return EMPTY;
      })
    ).subscribe();
  }


  onCancel(): void {
    this.location.back();
  }

  onAmountInput(event: Event): void {
    const sanitizedValue = restrictToTwoDecimals(event);
    const parsedValue = sanitizedValue ? Number.parseFloat(sanitizedValue) : null;
    if (parsedValue !== null && parsedValue <= Number(AppConstants.DEPOSIT_AMOUNT_MAX_LIMIT)) {
      this.amount = parsedValue;
    }
    this.validateAmount(this.amount);
  }

  private validateAmount(amount: number | null): void {
    this.amountErrorMessage = '';
    this.amountZero = false;
    this.isAmountPresentValid = false;

    // Check if amount is provided
    if (amount !== null && amount !== undefined) {
      if (amount === 0) {
        this.amountZero = true;
      } else if (amount > Number(AppConstants.DEPOSIT_AMOUNT_MAX_LIMIT)) {
        this.amountErrorMessage = 'PAYABLE_EXCEEDED';
      } else if (amount > 0) {
        this.isAmountPresentValid = true;
      }
    }
  }

  get isAmountInvalid(): boolean {
    return !!this.amountErrorMessage || this.amountZero;
  }

  get depositAmountLimit(): string {
    return AppConstants.showAmountFormatted(Number(AppConstants.DEPOSIT_AMOUNT_MAX_LIMIT));
  }

  setDepositFieldsMandatory(selectedCategoryCode: { value: string }): void {
    const value = selectedCategoryCode?.value;
    this.isPayerIdMandatory = this.getPayerIdMandatory(value);
    this.isCourtApplicable = value === AppConstants.OTHER_DEPOSIT;
    this.isEmailMandatory = value === AppConstants.SHERIFF_BAILIFF_DEPOSIT;
  }

  get isSheriffBailiffDeposit(): boolean {
    return this.depositCategory === AppConstants.SHERIFF_BAILIFF_DEPOSIT;
  }
}
